import { useToast } from '../../radix'
import { useSaveToList } from './saveToListContext'
import { useFavourites } from '../../../auth/hooks/useFavourites'
import type { FavouriteTarget } from '../../../auth/hooks/useFavourites'
import { ACTION_TOAST_MS, removedFromFavouritesMessage } from '../../../utils/favouriteLists'

export interface FavouriteToggleInput {
  target: FavouriteTarget
  /** Shown in the Save to list sheet if the engineer taps "Add to list". */
  productName: string
  /**
   * Set by callers that already know (favourite rows are always favourites),
   * so a tap before the id sets have loaded still un-hearts.
   */
  favourited?: boolean
}

/**
 * The one way to heart / un-heart a product (DEV-51010). Every heart in the
 * engineer app goes through here so they all behave the same:
 *
 * - Heart: saves at once (the icon flips optimistically), then toasts
 *   "Saved to favourites" with "Add to list", which opens the sheet.
 * - Un-heart: removes it from favourites and every list, then toasts
 *   "Removed from favourites and N lists" with Undo, which restores the
 *   favourite and those list memberships.
 *
 * The toasts wait for the server so they carry the favourite id and the
 * real list count, and never claim a save that failed.
 */
export function useFavouriteToggle() {
  const { isFavourited, addFavouriteAsync, removeFavouriteAsync, restoreFavouriteAsync } =
    useFavourites()
  const { addToast } = useToast()
  const { openSaveToList } = useSaveToList()

  const undo = (favouriteId: string, target: FavouriteTarget) => {
    restoreFavouriteAsync(favouriteId, target).catch(() => {
      addToast("Couldn't undo. Try again.", 'error')
    })
  }

  return ({ target, productName, favourited = isFavourited(target) }: FavouriteToggleInput) => {
    if (!favourited) {
      addFavouriteAsync(target)
        .then((favourite) => {
          addToast('Saved to favourites', 'success', ACTION_TOAST_MS, {
            label: 'Add to list',
            onClick: () => openSaveToList({ favouriteId: favourite.id, productName }),
          })
        })
        .catch(() => addToast("Couldn't save to favourites. Try again.", 'error'))
      return
    }

    removeFavouriteAsync(target)
      .then((removed) => {
        // Null: it wasn't a favourite on the server (stale heart). The icon
        // is already off, so there's nothing to undo.
        if (!removed) return
        addToast(removedFromFavouritesMessage(removed.removedListIds.length), 'info', ACTION_TOAST_MS, {
          label: 'Undo',
          onClick: () => undo(removed.favouriteId, target),
        })
      })
      .catch(() => addToast("Couldn't remove from favourites. Try again.", 'error'))
  }
}
